import React, { useState, useEffect, useRef } from 'react';

/**
 * AudioButton - Listen mode for learning content
 *
 * Uses Web Speech API for text-to-speech.
 * Works offline, free, no external dependencies.
 *
 * Design principles:
 * - Learner control (play/pause/stop)
 * - Not auto-playing
 * - Works while phone charges overnight
 * - Respects oral tradition of knowledge sharing
 * - Reading on phones is tiring - listening helps
 */
function AudioButton({ text, label = "🔊 Listen" }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const utteranceRef = useRef(null);

  useEffect(() => {
    // Check if browser supports speech synthesis
    if ('speechSynthesis' in window) {
      setIsSupported(true);
    }

    // Cleanup on unmount
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const handlePlay = () => {
    if (!text || !isSupported) return;

    // Stop if already playing
    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);

    // Configure voice settings
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1;

    // Event handlers
    utterance.onstart = () => setIsPlaying(true);
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);

    // Store reference
    utteranceRef.current = utterance;

    // Speak
    window.speechSynthesis.speak(utterance);
  };

  if (!isSupported) {
    return null; // Hide if not supported
  }

  return (
    <button
      onClick={handlePlay}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
        isPlaying
          ? 'bg-blue-600 border-blue-500 text-white'
          : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'
      }`}
      title={isPlaying ? 'Stop listening' : 'Listen to this section'}
    >
      <span className="text-lg">
        {isPlaying ? '⏸️' : '🔊'}
      </span>
      <span className="text-sm font-medium">
        {isPlaying ? 'Stop' : label}
      </span>
    </button>
  );
}

export default AudioButton;
